import type { MobileContentFrameWindow } from './types';

function isElementVisibleInViewport(element: Element, window: Window): boolean {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  const tooHigh = rect.bottom <= 0;
  const tooLow = rect.top >= viewportHeight;
  const tooLeft = rect.right <= 0;
  const tooRight = rect.left >= viewportWidth;

  if (tooHigh || tooLow || tooLeft || tooRight) {
    return false;
  }

  const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
  const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);

  return visibleWidth > 10 && visibleHeight > 10;
}

/**
 * Handles debug highlighting and indicator for the currently centered element.
 * This utility is used by both VerticalPaginatedScrollingManager and ContinuousScrollingManager
 * to show developers which element is detected as "centered" in the viewport.
 */
export function handleCenteredElementDebug(
  centeredElement: Element | null,
  window: MobileContentFrameWindow,
  document: Document,
): void {
  if (window.isCenteredElementDebugEnabled) {
    const prevElementDebug = document.querySelector('.centeredElementDebug');
    prevElementDebug?.classList.remove('centeredElementDebug', 'centeredElementDebug-visible');
    if (centeredElement) {
      const isVisible = isElementVisibleInViewport(centeredElement, window);
      centeredElement.classList.add('centeredElementDebug');
      if (isVisible) {
        centeredElement.classList.add('centeredElementDebug-visible');
      }
    }
    const existingIndicator = document.querySelector('.centered-element-debug-indicator');
    existingIndicator?.remove();

    if (centeredElement) {
      const isVisible = isElementVisibleInViewport(centeredElement, window);
      const indicator = document.createElement('div');
      indicator.className = isVisible ? 'centered-element-debug-indicator visible' : 'centered-element-debug-indicator not-visible';

      const idPart = centeredElement.id ? `#${centeredElement.id}` : '';
      const classes = centeredElement.className ? centeredElement.className.split(' ').filter((cls) => cls && cls !== 'centeredElementDebug' && cls !== 'centeredElementDebug-visible') : [];
      const classPart = classes.length > 0 ? `.${classes.slice(0, 2).join('.')}` : '';
      const visibilityStatus = isVisible ? ' [VISIBLE]' : ' [NOT VISIBLE]';
      indicator.textContent = `${centeredElement.tagName.toLowerCase()}${idPart}${classPart}${visibilityStatus}`;
      document.body.appendChild(indicator);
    }
  } else {
    const prevElementDebug = document.querySelector('.centeredElementDebug');
    prevElementDebug?.classList.remove('centeredElementDebug', 'centeredElementDebug-visible');
    const existingIndicator = document.querySelector('.centered-element-debug-indicator');
    existingIndicator?.remove();
  }
}
